import { useFrame } from '@react-three/fiber'
import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { Mesh, type Group, type Material, type MeshBasicMaterial } from 'three'
import { perfilParaEstado } from '../../dominio/caminhaoTanque/perfil'
import { PRODUTOS } from '../../dominio/produtos'
import { meteorologiaDaCena } from '../../dominio/tabletop'
import type {
  CenaTabletop,
  ObjetoTabletop,
  TransformacaoTabletop,
  TipoObjetoTabletop,
} from '../../dominio/tabletop'
import type { CamadasFonte } from '../../dominio/tabletop/camadas'
import { CaminhaoTanque3D } from '../caminhaoTanque/CaminhaoTanque3D'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { Biruta3D } from '../cenario/Biruta3D'
import { Pista3D } from '../cenario/Pista3D'
import { Terreno3D } from '../cenario/Terreno3D'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { AneisZona3D } from '../zonas/AneisZona3D'
import {
  ContornosPluma3D,
  AreaAcaoProtetora3D,
} from '../zonas/ContornosPluma3D'
import { FiguraBombeiro3D } from './figuras/FiguraBombeiro3D'
import { FiguraProfissional3D } from './figuras/FiguraProfissional3D'
import { girarVetorMesa } from '../../dominio/tabletop/encaixeRiscos'
import { FiguraRisco3D } from './figuras/FiguraRisco3D'
import { FiguraPredio3D } from './figuras/FiguraPredio3D'
import { FiguraViaturaCivil3D } from './figuras/FiguraViaturaCivil3D'
import { FiguraViaturaEspecial3D } from './figuras/FiguraViaturaEspecial3D'
import { FiguraVitima3D } from './figuras/FiguraVitima3D'
import { useManipulacaoMesa3D } from './useManipulacaoMesa3D'
import { PecaDetalhe3D } from './figuras/PecaDetalhe3D'
import {
  FiguraAreaApoio3D,
  FiguraBueiro3D,
  FiguraCone3D,
  FiguraFita3D,
} from './figuras/FiguraApoio3D'

/** Peça arredondada (ver `PecaDetalhe3D`) — cantos duros ficaram só no caminhão de antes do projeto ter essa peça. */
function Bloco({
  tamanho,
  cor,
  y,
  metal,
  rugosidade,
  raio = 0.03,
}: {
  tamanho: [number, number, number]
  cor: string
  y?: number
  metal?: number
  rugosidade?: number
  raio?: number
}) {
  return (
    <PecaDetalhe3D
      posicao={[0, y ?? tamanho[1] / 2, 0]}
      tamanho={tamanho}
      cor={cor}
      metal={metal}
      rugosidade={rugosidade}
      raio={raio}
    />
  )
}

/** Halo pulsante sob um bombeiro/viatura dentro do isolamento do GRE de alguma fonte. */
function HaloRisco3D() {
  const material = useRef<MeshBasicMaterial>(null)
  useFrame(({ clock }) => {
    if (material.current)
      material.current.opacity = 0.22 + 0.18 * Math.sin(clock.elapsedTime * 4)
  })
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.04, 0]}
      raycast={() => null}
    >
      <circleGeometry args={[1.3, 28]} />
      <meshBasicMaterial
        ref={material}
        color="#ef4444"
        transparent
        opacity={0.3}
        depthWrite={false}
      />
    </mesh>
  )
}

function FiguraObjeto({
  objeto,
  cena,
}: {
  objeto: ObjetoTabletop
  cena: CenaTabletop
}): ReactNode {
  const { tipo } = objeto
  const produto = PRODUTOS.find((p) => p.id === objeto.fonte?.idProduto)
  const cor = objeto.cor ?? '#e5a34e'
  if (tipo === 'caminhaoTanque')
    return (
      <CaminhaoTanque3D
        mostrarSinalizacao={Boolean(produto)}
        perfil={perfilParaEstado(
          produto?.estadoTransporte ?? 'gasLiquefeitoPressurizado',
        )}
        numeroRisco={produto?.numeroRisco.valor ?? ''}
        onu={produto?.onu ?? 0}
        classes={
          produto ? [produto.classe.valor, ...produto.riscosSubsidiarios] : []
        }
      />
    )
  if (tipo === 'biruta') {
    const m = meteorologiaDaCena(cena)
    return (
      <Biruta3D
        posicao={[0, 0]}
        direcaoVentoGraus={
          m.direcaoVentoGraus -
          (objeto.transformacao.rotacao[1] * 180) / Math.PI
        }
        velocidadeMS={m.ventoMS}
      />
    )
  }
  if (tipo === 'predio' || tipo === 'escola' || tipo === 'hospital')
    return <FiguraPredio3D tipo={tipo} cor={cor} />
  if (tipo === 'corrego')
    return (
      <>
        <Bloco tamanho={[8, 0.08, 120]} cor="#4f98bf" />
        {[-4.2, 4.2].map((x) => (
          <mesh key={x} position={[x, 0.06, 0]}>
            <boxGeometry args={[0.6, 0.12, 120]} />
            <meshStandardMaterial color="#6b5339" />
          </mesh>
        ))}
      </>
    )
  if (tipo === 'bueiro') return <FiguraBueiro3D />
  if (tipo === 'cone') return <FiguraCone3D />
  if (tipo === 'fita') return <FiguraFita3D />
  if (
    tipo === 'corredorDescontaminacao' ||
    tipo === 'areaVitimas' ||
    tipo === 'postoComando'
  )
    return <FiguraAreaApoio3D tipo={tipo} />
  if (tipo === 'vitima')
    return (
      <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.19, 0]}>
        <FiguraVitima3D />
      </group>
    )
  if (
    tipo === 'bombeiroNivelA' ||
    tipo === 'bombeiroNivelB' ||
    tipo === 'bombeiroNivelC'
  )
    return <FiguraBombeiro3D nivel={tipo} />
  if (tipo === 'bombeiroIncendio' || tipo === 'medico')
    return <FiguraProfissional3D tipo={tipo} />
  if (tipo === 'vazamento' || tipo === 'liquidoDerramado' || tipo === 'fogo' || tipo === 'fogoTanque' || tipo === 'curtoCircuito' || tipo === 'bleve')
    return <FiguraRisco3D tipo={tipo} vinculado={!!objeto.idTanque} alturaOrigem={objeto.idTanque ? objeto.transformacao.posicao[1] : 1.25} direcaoSaida={girarVetorMesa([0, 0, 1], cena.objetos.find(o => o.id === objeto.idTanque)?.transformacao.rotacao ?? [0, 0, 0])} />
  if (tipo === 'viaturaHazmat' || tipo === 'viaturaAbt')
    return <FiguraViaturaEspecial3D tipo={tipo} />
  if (tipo === 'ambulancia' || tipo === 'carro')
    return <FiguraViaturaCivil3D tipo={tipo} cor={cor} />
  return null
}

export function CenaMesa3D({
  cena,
  camadas,
  mostrarCamadas,
  selecionado,
  aoSelecionar,
  idsEmRisco,
  tipoParaPosicionar,
  aoPosicionar,
  aoTransformar,
  aoManipular,
  aoCancelarPosicionamento,
  previaObjeto,
  passoPosicaoM,
  passoGiroGraus,
}: {
  cena: CenaTabletop
  camadas: readonly CamadasFonte[]
  mostrarCamadas: boolean
  selecionado?: string | null
  aoSelecionar?: (id: string | null) => void
  /** Bombeiros/viaturas dentro do isolamento do GRE de alguma fonte — mecânica leve de risco. */
  idsEmRisco?: ReadonlySet<string>
  previaObjeto?: ObjetoTabletop | null
  passoPosicaoM?: number | null
  passoGiroGraus?: number | null
  tipoParaPosicionar?: TipoObjetoTabletop | null
  aoPosicionar?: (ponto: readonly [number, number]) => void
  aoTransformar?: (id: string, transformacao: TransformacaoTabletop) => void
  aoManipular?: (ativa: boolean) => void
  aoCancelarPosicionamento?: () => void
}) {
  const m = meteorologiaDaCena(cena)
  const interacao = useManipulacaoMesa3D({
    objetos: cena.objetos,
    posicionando: Boolean(tipoParaPosicionar),
    aoTransformar,
    aoManipular,
    aoCancelarPosicionamento,
    passoPosicaoM,
    passoGiroGraus,
  })
  const objetoSelecionado = cena.objetos.find(
    (o) => o.id === selecionado && !o.oculto,
  )
  const tocarChao = (
    e: import('@react-three/fiber').ThreeEvent<MouseEvent>,
  ) => {
    e.stopPropagation()
    if (!interacao.foiClique(e)) return
    if (tipoParaPosicionar) {
      const ponto = interacao.pontoNoChao(e.nativeEvent)
      if (ponto) aoPosicionar?.(ponto)
    } else aoSelecionar?.(null)
  }
  return (
    <>
      <Terreno3D ladoM={24000} />
      <Pista3D comprimentoM={12000} />
      <mesh
        name="solo-posicionamento-mesa"
        position={[0, 0.025, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={tocarChao}
      >
        <planeGeometry args={[48000, 48000]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {tipoParaPosicionar && (
        <group name="previa-posicionamento" ref={interacao.previaPosicao} visible={false}>
          {previaObjeto && (
            <PreviaObjeto3D
              key={previaObjeto.tipo}
              objeto={previaObjeto}
              cena={cena}
            />
          )}
          <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
            <ringGeometry args={[0.75, 1, 32]} />
            <meshBasicMaterial color="#7dd3fc" depthTest={false} />
          </mesh>
        </group>
      )}
      {cena.objetos
        .filter((o) => !o.oculto && !cena.objetos.find(t => t.id === o.idTanque)?.oculto)
        .map((o) => (
          <group
            key={o.id}
            ref={(grupo) => {
              if (grupo) interacao.grupos.current.set(o.id, grupo)
              else interacao.grupos.current.delete(o.id)
            }}
            name={`${PREFIXO_SELECIONAVEL}${o.id}`}
            position={[...o.transformacao.posicao]}
            rotation={[...o.transformacao.rotacao]}
            scale={[...o.transformacao.escala]}
            onPointerDown={(e) => {
              if (tipoParaPosicionar || e.button !== 0) return
              e.stopPropagation()
              aoSelecionar?.(o.id)
              interacao.iniciar(o.id, 'mover', e)
            }}
            onClick={(e) => {
              if (tipoParaPosicionar) {
                tocarChao(e)
                return
              }
              e.stopPropagation()
              if (interacao.foiClique(e)) aoSelecionar?.(o.id)
            }}
          >
            <FiguraObjeto objeto={o} cena={cena} />
            {o.tipo === 'vitima' && (
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
                <circleGeometry args={[0.6, 24]} />
                <meshBasicMaterial color="#ef4444" />
              </mesh>
            )}
            {idsEmRisco?.has(o.id) && <HaloRisco3D />}
            {selecionado === o.id && (
              <Rotulo3D posicao={[0, 6, 0]} texto={o.nome} fator={100} />
            )}
          </group>
        ))}
      {objetoSelecionado &&
        !objetoSelecionado.bloqueado &&
        !tipoParaPosicionar &&
        aoTransformar && (
          <group
            ref={interacao.alca}
            position={[
              objetoSelecionado.transformacao.posicao[0],
              objetoSelecionado.transformacao.posicao[1] + 0.06,
              objetoSelecionado.transformacao.posicao[2],
            ]}
            rotation={[0, objetoSelecionado.transformacao.rotacao[1], 0]}
          >
            <mesh
              rotation={[-Math.PI / 2, 0, 0]}
              onPointerDown={(e) =>
                interacao.iniciar(objetoSelecionado.id, 'girar', e)
              }
              onClick={(e) => e.stopPropagation()}
            >
              <ringGeometry
                args={[
                  raioAlca(objetoSelecionado) - 0.08,
                  raioAlca(objetoSelecionado) + 0.08,
                  64,
                ]}
              />
              <meshBasicMaterial color="#fbbf24" depthTest={false} />
            </mesh>
            <mesh
              position={[0, 0.15, raioAlca(objetoSelecionado)]}
              onPointerDown={(e) =>
                interacao.iniciar(objetoSelecionado.id, 'girar', e)
              }
              onClick={(e) => e.stopPropagation()}
            >
              <sphereGeometry
                args={[
                  Math.max(0.16, raioAlca(objetoSelecionado) * 0.045),
                  12,
                  8,
                ]}
              />
              <meshBasicMaterial color="#fbbf24" depthTest={false} />
            </mesh>
          </group>
        )}
      {mostrarCamadas &&
        camadas.map((c) => (
          <group key={c.objeto.id} position={[c.origem[0], 0, c.origem[1]]}>
            <AneisZona3D
              aneis={[
                {
                  id: 'gre',
                  raioM: c.isolamentoGreM,
                  cor: '#60a5fa',
                  tracejado: true,
                  rotulo: 'GRE: isolar',
                },
                ...(c.bleve
                  ? [
                      {
                        id: 'morna',
                        raioM: c.bleve.zonas.raioMornaM,
                        cor: '#fbbf24',
                        preenchido: true,
                        rotulo: 'ZM',
                      },
                      {
                        id: 'quente',
                        raioM: c.bleve.zonas.raioQuenteM,
                        cor: '#ef4444',
                        preenchido: true,
                        rotulo: 'ZQ',
                      },
                    ]
                  : []),
              ]}
            />
            <ContornosPluma3D
              direcaoVentoGraus={m.direcaoVentoGraus}
              contornos={[...(c.pluma?.contornos ?? [])]
                .reverse()
                .map((p, i) => ({
                  id: p.limite.id,
                  borda: p.borda,
                  cor: ['#facc15', '#f97316', '#ef4444', '#b91c1c'][i % 4]!,
                  opacidade: 0.16,
                  rotulo: p.limite.rotulo,
                }))}
            >
              {c.acaoProtetora && (
                <AreaAcaoProtetora3D
                  isolarM={c.acaoProtetora.isolarM}
                  protegerM={c.acaoProtetora.protegerM}
                />
              )}
            </ContornosPluma3D>
          </group>
        ))}
    </>
  )
}

function PreviaObjeto3D({
  objeto,
  cena,
}: {
  objeto: ObjetoTabletop
  cena: CenaTabletop
}) {
  const grupo = useRef<Group>(null)
  useLayoutEffect(() => {
    const restaurar: (() => void)[] = []
    grupo.current?.traverse((o) => {
      if (!(o instanceof Mesh)) return
      const original = o.material as Material | Material[]
      const raycast = o.raycast
      const castShadow = o.castShadow
      const receiveShadow = o.receiveShadow
      const clonar = (m: Material) => {
        const copia = m.clone()
        copia.transparent = true
        copia.opacity = 0.4
        copia.depthWrite = false
        return copia
      }
      const copia = Array.isArray(original)
        ? original.map(clonar)
        : clonar(original)
      o.material = copia
      o.raycast = () => null
      o.castShadow = false
      o.receiveShadow = false
      restaurar.push(() => {
        o.material = original
        o.raycast = raycast
        o.castShadow = castShadow
        o.receiveShadow = receiveShadow
        ;(Array.isArray(copia) ? copia : [copia]).forEach((m) => m.dispose())
      })
    })
    return () => restaurar.forEach((fn) => fn())
  }, [objeto])
  return (
    <group
      ref={grupo}
      position={[0, objeto.transformacao.posicao[1], 0]}
      rotation={[...objeto.transformacao.rotacao]}
      scale={[...objeto.transformacao.escala]}
    >
      <FiguraObjeto objeto={objeto} cena={cena} />
    </group>
  )
}

function raioAlca(objeto: ObjetoTabletop): number {
  const raios: Partial<Record<TipoObjetoTabletop, number>> = {
    caminhaoTanque: 10,
    carro: 2.6,
    ambulancia: 3.4,
    viaturaHazmat: 3.5,
    viaturaAbt: 3.5,
    predio: 19,
    escola: 19,
    hospital: 19,
    corrego: 61,
    fita: 10.8,
    postoComando: 12,
    areaVitimas: 12,
    corredorDescontaminacao: 11,
    vitima: 2.2,
  }
  return (
    (raios[objeto.tipo] ?? 1) *
    Math.max(
      Math.abs(objeto.transformacao.escala[0]),
      Math.abs(objeto.transformacao.escala[2]),
    )
  )
}
